// state.ts — all state + pure derivations for the Add New Product (V6) screen.
// Design source: docs/design/design_handoff_add_new_product/ (README + newproduct-v6.jsx).

export type ProductKind = "new" | "existing";
export type FeatureScope = "single" | "multi";
export type PrimaryFormFactor = "web" | "mobile" | "api";
export type FieldKey = "name" | "problem" | "solution";
export type ListKey = "users" | "team";
export type NfrKey = "general" | "edge_cases" | "latency" | "data_formats" | "schema_constraints" | "error_handling" | "system_constraints" | "compliance";
export type SourceId = "files" | "jira" | "azure" | "github" | "gitlab" | "bitbucket" | "azuregit";

export interface NfrFieldDefinition {
    key: NfrKey;
    inferField: `nfr_${NfrKey}`;
    variable: string;
    label: string;
    placeholder: string;
    maxLength: number;
}

export const NFR_FIELDS: readonly NfrFieldDefinition[] = [
    {
        key: "general",
        inferField: "nfr_general",
        variable: "general-nonfunctional-requirements",
        label: "General Non-Functional Requirements",
        placeholder: "Availability, scalability, accessibility, or other quality expectations…",
        maxLength: 2000
    },
    {
        key: "edge_cases",
        inferField: "nfr_edge_cases",
        variable: "edge-cases-exception-handling",
        label: "Edge Cases & Exception Handling",
        placeholder: "Empty states, concurrent edits, partial uploads, timeouts, or offline behavior…",
        maxLength: 2000
    },
    {
        key: "latency",
        inferField: "nfr_latency",
        variable: "response-time-latency-thresholds",
        label: "Response Time & Latency Thresholds",
        placeholder: "P95 page load, API response, throughput, or batch completion targets…",
        maxLength: 1000
    },
    {
        key: "data_formats",
        inferField: "nfr_data_formats",
        variable: "data-formats-encoding",
        label: "Data Formats & Encoding",
        placeholder: "JSON/UTF-8 APIs, CSV exports, ISO-8601 timestamps, or numeric precision…",
        maxLength: 1000
    },
    {
        key: "schema_constraints",
        inferField: "nfr_schema_constraints",
        variable: "schema-constraints",
        label: "Schema Constraints",
        placeholder: "Required fields, uniqueness, referential integrity, lengths, or enumerations…",
        maxLength: 1500
    },
    {
        key: "error_handling",
        inferField: "nfr_error_handling",
        variable: "error-handling-requirements",
        label: "Error-Handling Requirements",
        placeholder: "User-facing errors, retries, idempotency, logging, or alerting expectations…",
        maxLength: 1500
    },
    {
        key: "system_constraints",
        inferField: "nfr_system_constraints",
        variable: "system-constraints",
        label: "System Constraints",
        placeholder: "Required platforms, hosting, integrations, technology, or operational limits…",
        maxLength: 1500
    },
    {
        key: "compliance",
        inferField: "nfr_compliance",
        variable: "external-compliance-requirements",
        label: "External Compliance Requirements",
        placeholder: "GDPR, HIPAA, SOC 2, PCI-DSS, accessibility law, or data residency…",
        maxLength: 1500
    }
] as const;

export const emptyNfrValues = (): Record<NfrKey, string> => ({
    general: "",
    edge_cases: "",
    latency: "",
    data_formats: "",
    schema_constraints: "",
    error_handling: "",
    system_constraints: "",
    compliance: ""
});

export interface SourceSelectionItem {
    id: string;
    key?: string;
    name: string;
}
export interface SourceStaged {
    processJobId?: string;
    repoIds?: number[];
    fileIds?: number[];
    importedCount?: number;
}
export interface SourceConnection {
    connected: boolean;
    account?: string;
    selection?: SourceSelectionItem[];
    staged?: SourceStaged;
    /** Existing product-link rows loaded in refresh mode. */
    existingLinkIds?: number[];
    /** Current linked-item count; unlike staged.importedCount this is not pending data. */
    syncedCount?: number;
}

export interface NewProductState {
    kind: ProductKind | null;
    scope: FeatureScope | null;
    primaryFormFactor: PrimaryFormFactor | null;
    fields: Record<FieldKey, string>;
    nfr: Record<NfrKey, string>;
    users: string[];
    team: string[];
    connections: Record<SourceId, SourceConnection>;
    meshOpen: boolean;
    isCreating: boolean;
}

export interface NewProductDraftSnapshot {
    kind: ProductKind | null;
    scope: FeatureScope | null;
    primaryFormFactor: PrimaryFormFactor | null;
    fields: Record<FieldKey, string>;
    nfr: Record<NfrKey, string>;
    users: string[];
    team: string[];
    connectedSources: SourceId[];
    contextMeshAnalysis: {
        status: "unavailable" | "analyzing" | "ready" | "stale" | "failed";
        score: number | null;
        tier: "GOOD" | "BETTER" | "BEST" | null;
        analyzedDraftVersion: number | null;
    };
    dirty: boolean;
    hasUnsavedChanges: boolean;
}

export interface NewProductDraftPatch {
    kind?: ProductKind;
    scope?: FeatureScope;
    productName?: string;
    problemProductSolves?: string;
    solutionProductOffers?: string;
    primaryFormFactor?: PrimaryFormFactor | null;
    targetUsers?: string[];
    teamMembers?: string[];
    nonFunctionalRequirements?: Partial<Record<NfrKey, string>>;
}

export interface PersistedNewProductDraftState {
    schema_version: 3;
    state: Pick<NewProductState, "kind" | "scope" | "primaryFormFactor" | "fields" | "nfr" | "users" | "team" | "connections">;
}

// Primary Form Factor is required: the form starts on this value and no code
// path may leave it empty. `PrimaryFormFactor | null` stays in the types for
// the transport/patch surfaces (persisted drafts written before it was
// required, and MUI's null-on-deselect), which all normalize back to this.
export const DEFAULT_PRIMARY_FORM_FACTOR: PrimaryFormFactor = "web";

export const initialNewProductState: NewProductState = {
    kind: null,
    scope: null,
    primaryFormFactor: DEFAULT_PRIMARY_FORM_FACTOR,
    fields: { name: "", problem: "", solution: "" },
    nfr: emptyNfrValues(),
    users: [],
    team: [],
    connections: {
        files: { connected: false },
        jira: { connected: false },
        azure: { connected: false },
        github: { connected: false },
        gitlab: { connected: false },
        bitbucket: { connected: false },
        azuregit: { connected: false }
    },
    meshOpen: false,
    isCreating: false
};

export type NewProductAction =
    | { type: "setKind"; kind: ProductKind }
    | { type: "setScope"; scope: FeatureScope }
    | { type: "setPrimaryFormFactor"; primaryFormFactor: PrimaryFormFactor | null }
    | { type: "resetKind" }
    | { type: "setField"; field: FieldKey; value: string }
    | { type: "clearField"; field: FieldKey }
    | { type: "setNfrField"; field: NfrKey; value: string }
    | { type: "clearNfrField"; field: NfrKey }
    | { type: "setListItem"; list: ListKey; index: number; value: string }
    | { type: "addListItem"; list: ListKey }
    | { type: "removeListItem"; list: ListKey; index: number }
    | { type: "clearList"; list: ListKey }
    | { type: "appendListItems"; list: ListKey; values: string[] }
    | { type: "toggleMesh" }
    | { type: "setCreating"; creating: boolean }
    | { type: "applyDraftPatch"; patch: NewProductDraftPatch }
    | { type: "hydrate"; state: NewProductState }
    | { type: "reset" }
    | { type: "setConnection"; source: SourceId; connection: SourceConnection }
    | { type: "disconnectSource"; source: SourceId };

export function applyNewProductDraftPatch(state: NewProductState, patch: NewProductDraftPatch): NewProductState {
    const fields = { ...state.fields };
    if (patch.productName !== undefined) fields.name = patch.productName;
    if (patch.problemProductSolves !== undefined) fields.problem = patch.problemProductSolves;
    if (patch.solutionProductOffers !== undefined) fields.solution = patch.solutionProductOffers;
    const nfr = { ...state.nfr, ...(patch.nonFunctionalRequirements ?? {}) };

    return {
        ...state,
        kind: patch.kind === undefined ? state.kind : patch.kind,
        // Scope only applies to Add to Existing Product. Apply it in the same
        // patch as kind so FDE can answer both gate questions in one turn.
        scope: patch.kind === "new" ? null : patch.scope === undefined ? state.scope : patch.scope,
        primaryFormFactor: patch.primaryFormFactor === undefined ? state.primaryFormFactor : (patch.primaryFormFactor ?? DEFAULT_PRIMARY_FORM_FACTOR),
        fields,
        nfr,
        users: patch.targetUsers === undefined ? state.users : [...patch.targetUsers],
        team: patch.teamMembers === undefined ? state.team : [...patch.teamMembers]
    };
}

export function newProductReducer(state: NewProductState, action: NewProductAction): NewProductState {
    switch (action.type) {
        case "setKind":
            return { ...state, kind: action.kind, scope: action.kind === "existing" ? state.scope : null };
        case "setScope":
            return { ...state, scope: action.scope };
        case "setPrimaryFormFactor":
            // Required field: MUI's exclusive ToggleButtonGroup sends null when
            // the active pill is clicked again — keep the current selection.
            return { ...state, primaryFormFactor: action.primaryFormFactor ?? state.primaryFormFactor };
        case "resetKind":
            return { ...state, kind: null, scope: null };
        case "setField":
            return { ...state, fields: { ...state.fields, [action.field]: action.value } };
        case "clearField":
            return { ...state, fields: { ...state.fields, [action.field]: "" } };
        case "setNfrField":
            return { ...state, nfr: { ...state.nfr, [action.field]: action.value } };
        case "clearNfrField":
            return { ...state, nfr: { ...state.nfr, [action.field]: "" } };
        case "setListItem":
            return {
                ...state,
                [action.list]: state[action.list].map((v, i) => (i === action.index ? action.value : v))
            };
        case "addListItem":
            return { ...state, [action.list]: [...state[action.list], ""] };
        case "removeListItem":
            return { ...state, [action.list]: state[action.list].filter((_, i) => i !== action.index) };
        case "clearList":
            return { ...state, [action.list]: [] };
        case "appendListItems": {
            const kept = state[action.list].filter(v => v.trim() !== "");
            const seen = new Set(kept.map(v => v.trim().toLowerCase()));
            const additions: string[] = [];
            for (const raw of action.values) {
                const value = raw.trim();
                if (!value || seen.has(value.toLowerCase())) continue;
                seen.add(value.toLowerCase());
                additions.push(value);
            }
            if (additions.length === 0) return state;
            return { ...state, [action.list]: [...kept, ...additions] };
        }
        case "toggleMesh":
            return { ...state, meshOpen: !state.meshOpen };
        case "setCreating":
            return { ...state, isCreating: action.creating };
        case "applyDraftPatch":
            return applyNewProductDraftPatch(state, action.patch);
        case "hydrate":
            return action.state;
        case "reset":
            return {
                ...initialNewProductState,
                fields: { ...initialNewProductState.fields },
                nfr: emptyNfrValues(),
                connections: { ...initialNewProductState.connections }
            };
        case "setConnection":
            return { ...state, connections: { ...state.connections, [action.source]: action.connection } };
        case "disconnectSource":
            return { ...state, connections: { ...state.connections, [action.source]: { connected: false } } };
        default:
            return state;
    }
}

// Lock rule (handoff §Interactions): nothing usable until kind is chosen,
// and for existing products, until scope is chosen too.
export function isLocked(s: NewProductState): boolean {
    return s.kind === null || (s.kind === "existing" && s.scope === null);
}

export function canCreate(s: NewProductState): boolean {
    return (
        s.kind !== null &&
        (s.kind !== "existing" || s.scope !== null) &&
        s.fields.name.trim() !== "" &&
        s.fields.problem.trim() !== "" &&
        s.primaryFormFactor !== null &&
        Object.keys(nfrValidationErrors(s)).length === 0 &&
        !s.isCreating
    );
}

export function isNewProductDraftDirty(s: NewProductState): boolean {
    return (
        s.kind !== null ||
        s.scope !== null ||
        // Compares against the default, not null: the field is pre-selected, so
        // an untouched form must still read clean (no spurious draft saves or
        // unsaved-changes prompts).
        s.primaryFormFactor !== DEFAULT_PRIMARY_FORM_FACTOR ||
        Object.values(s.fields).some(value => value.trim() !== "") ||
        Object.values(s.nfr).some(value => value.trim() !== "") ||
        s.users.some(value => value.trim() !== "") ||
        s.team.some(value => value.trim() !== "") ||
        Object.values(s.connections).some(
            c => c.connected || Boolean(c.staged && ((c.staged.fileIds?.length ?? 0) > 0 || (c.staged.repoIds?.length ?? 0) > 0 || c.staged.processJobId))
        )
    );
}

export function collectStagedIds(s: NewProductState): { processJobIds: string[]; repoIds: number[]; fileIds: number[] } {
    const processJobIds: string[] = [];
    const repoIds: number[] = [];
    const fileIds: number[] = [];
    for (const conn of Object.values(s.connections)) {
        if (conn.staged?.processJobId) processJobIds.push(conn.staged.processJobId);
        if (conn.staged?.repoIds) repoIds.push(...conn.staged.repoIds);
        if (conn.staged?.fileIds) fileIds.push(...conn.staged.fileIds);
    }
    return { processJobIds, repoIds, fileIds };
}

/**
 * Stable identity for inputs the canonical draft analyzer can actually read.
 * Connection dialog/account/selection churn is deliberately excluded: a
 * source becomes analysis-relevant only after its staged import is complete.
 */
export function contextMeshAnalysisRevisionKey(s: NewProductState): string {
    const staged = collectStagedIds(s);
    return JSON.stringify({
        primaryFormFactor: s.primaryFormFactor,
        fields: Object.fromEntries(Object.entries(s.fields).map(([key, value]) => [key, value.trim()])),
        nfr: Object.fromEntries(Object.entries(s.nfr).map(([key, value]) => [key, value.trim()])),
        users: s.users.map(value => value.trim()).filter(Boolean),
        team: s.team.map(value => value.trim()).filter(Boolean),
        staged: {
            processJobIds: [...new Set(staged.processJobIds)].sort(),
            repoIds: [...new Set(staged.repoIds)].sort((a, b) => a - b),
            fileIds: [...new Set(staged.fileIds)].sort((a, b) => a - b)
        }
    });
}

/**
 * Stable identity for the effective Context Mesh inputs on Product Refresh.
 * Unlike creation drafts, refresh previews must also distinguish retained,
 * removed, and replaced product-scoped sources. Display-only connection
 * metadata is deliberately excluded.
 */
export function refreshContextMeshAnalysisRevisionKey(s: NewProductState): string {
    const base = JSON.parse(contextMeshAnalysisRevisionKey(s)) as Record<string, unknown>;
    const connections = Object.fromEntries(
        (Object.entries(s.connections) as [SourceId, SourceConnection][]).map(([source, connection]) => [
            source,
            {
                connected: connection.connected,
                existingLinkIds: [...new Set(connection.existingLinkIds ?? [])].sort((a, b) => a - b),
                selectionIds: [...new Set((connection.selection ?? []).map(item => item.id))].sort(),
                processJobId: connection.staged?.processJobId ?? null,
                repoIds: [...new Set(connection.staged?.repoIds ?? [])].sort((a, b) => a - b),
                fileIds: [...new Set(connection.staged?.fileIds ?? [])].sort((a, b) => a - b)
            }
        ])
    );
    return JSON.stringify({ ...base, connections });
}

export type StepId = "kind" | "scope" | "name" | "problem" | "solution" | "formFactor" | "nfr" | "users" | "team" | "integrations";

export function railSteps(s: NewProductState): { id: StepId; label: string; state: "done" | "active" | "open" }[] {
    const defs: { id: StepId; label: string; done: boolean; optional?: boolean }[] = [
        { id: "kind", label: "New / Existing", done: s.kind !== null },
        { id: "scope", label: "Single / Multiple Features", done: s.scope !== null },
        { id: "name", label: "Name", done: s.fields.name.trim() !== "" },
        { id: "problem", label: "Problem", done: s.fields.problem.trim() !== "" },
        { id: "solution", label: "Solution", done: s.fields.solution.trim() !== "" },
        { id: "formFactor", label: "Primary Form Factor", done: s.primaryFormFactor !== null },
        { id: "nfr", label: "Non-Functional", done: Object.values(s.nfr).some(value => value.trim() !== ""), optional: true },
        { id: "users", label: "Target Users", done: s.users.some(u => u.trim() !== "") },
        { id: "team", label: "Team Members", done: s.team.some(t => t.trim() !== "") },
        { id: "integrations", label: "Integrations", done: Object.values(s.connections).some(c => c.connected) }
    ];
    const visible = defs.filter(d => d.id !== "scope" || s.kind === "existing");
    const firstOpen = visible.findIndex(d => !d.done && !d.optional);
    return visible.map((d, i) => ({
        id: d.id,
        label: d.label,
        state: d.done ? "done" : i === firstOpen ? "active" : "open"
    }));
}

// "Shawn Best — Product Manager" -> { name, role }. Accepts em/en dash or
// hyphen surrounded by whitespace; no separator means the whole row is a name.
export function parseTeamMember(row: string): { name: string; role: string } {
    const match = row.match(/\s+[—–-]\s+/);
    if (!match || match.index === undefined) return { name: row.trim(), role: "" };
    return {
        name: row.slice(0, match.index).trim(),
        role: row.slice(match.index + match[0].length).trim()
    };
}

export interface ProductMeshSubmission {
    working_name: string;
    form_factor: PrimaryFormFactor;
    creation_mode?: "add_to_existing_product";
    feature_scope?: FeatureScope;
    requested_feature_count?: number;
    mesh: {
        working_name: string;
        pitch: string;
        problem: string;
        form_factor: PrimaryFormFactor;
        creation_mode?: "add_to_existing_product";
        feature_scope?: FeatureScope;
        requested_feature_count?: number;
        personas: string[];
        team_composition: { roles: { name: string; role: string }[] };
        non_functional_requirements?: Partial<Record<NfrKey, string>>;
        artifact_uploads: string[];
        integrations: Record<string, { selection: SourceSelectionItem[]; staged: SourceStaged }>;
    };
}

export function buildProductMeshSubmission(s: NewProductState): ProductMeshSubmission {
    const workingName = s.fields.name.trim();
    const problem = s.fields.problem.trim();
    const pitch = s.fields.solution.trim() || problem;
    const formFactor = s.primaryFormFactor ?? "web";
    const integrations = Object.fromEntries(
        Object.entries(s.connections)
            .filter(([, conn]) => conn.connected)
            .map(([source, conn]) => [source, { selection: conn.selection ?? [], staged: conn.staged ?? {} }])
    ) as Record<string, { selection: SourceSelectionItem[]; staged: SourceStaged }>;
    const nonFunctionalRequirements = Object.fromEntries(
        (Object.entries(s.nfr) as [NfrKey, string][]).map(([key, value]) => [key, value.trim()] as const).filter(([, value]) => value !== "")
    ) as Partial<Record<NfrKey, string>>;

    const featureConstraint =
        s.kind === "existing" && s.scope
            ? {
                  creation_mode: "add_to_existing_product" as const,
                  feature_scope: s.scope,
                  requested_feature_count: s.scope === "single" ? 1 : undefined
              }
            : {};

    return {
        working_name: workingName,
        form_factor: formFactor,
        ...featureConstraint,
        mesh: {
            working_name: workingName,
            pitch,
            problem,
            form_factor: formFactor,
            ...featureConstraint,
            personas: s.users.map(user => user.trim()).filter(Boolean),
            team_composition: {
                roles: s.team
                    .map(member => member.trim())
                    .filter(Boolean)
                    .map(parseTeamMember)
            },
            ...(Object.keys(nonFunctionalRequirements).length > 0 ? { non_functional_requirements: nonFunctionalRequirements } : {}),
            artifact_uploads: [],
            integrations
        }
    };
}

export function buildNewProductDraftSnapshot(
    s: NewProductState,
    contextMeshAnalysis: NewProductDraftSnapshot["contextMeshAnalysis"] = {
        status: "unavailable",
        score: null,
        tier: null,
        analyzedDraftVersion: null
    }
): NewProductDraftSnapshot {
    return {
        kind: s.kind,
        scope: s.scope,
        primaryFormFactor: s.primaryFormFactor,
        fields: { ...s.fields },
        nfr: { ...s.nfr },
        users: [...s.users],
        team: [...s.team],
        connectedSources: (Object.entries(s.connections) as [SourceId, SourceConnection][])
            .filter(([, conn]) => conn.connected)
            .map(([source]) => source),
        contextMeshAnalysis,
        dirty: isNewProductDraftDirty(s),
        hasUnsavedChanges: false
    };
}

export function buildPersistedNewProductDraftState(s: NewProductState): PersistedNewProductDraftState {
    return {
        schema_version: 3,
        state: {
            kind: s.kind,
            scope: s.scope,
            primaryFormFactor: s.primaryFormFactor,
            fields: { ...s.fields },
            nfr: { ...s.nfr },
            users: [...s.users],
            team: [...s.team],
            connections: { ...s.connections }
        }
    };
}

export function hydrateNewProductState(draft: PersistedNewProductDraftState): NewProductState {
    const persisted = draft.state;
    return {
        ...initialNewProductState,
        kind: persisted.kind,
        scope: persisted.scope,
        primaryFormFactor: persisted.primaryFormFactor ?? DEFAULT_PRIMARY_FORM_FACTOR,
        fields: { ...initialNewProductState.fields, ...persisted.fields },
        nfr: { ...emptyNfrValues(), ...(persisted.nfr ?? {}) },
        users: [...persisted.users],
        team: [...persisted.team],
        connections: { ...initialNewProductState.connections, ...persisted.connections }
    };
}

// Maps a productRefresh RefreshSource (Task 4's loader output) into the
// NewProductState shape for the `hydrate` action (KAN-1464 Task 5). Refresh
// mode always hydrates with kind: "new" — KindPills/scope pills are not
// rendered in refresh mode (a static product-name badge takes their place),
// so "new" is just the shape that keeps `canCreate`/lock helpers consistent
// with the rest of the reducer. Existing product integrations are supplied by
// the refresh loader and merged over the disconnected defaults.
// Note: RefreshCoreValues.type and description are intentionally not hydrated;
// NewProductState has no type/description fields.
export function hydrateStateFromRefreshCore(
    core: {
        name: string;
        problem: string;
        solution: string;
        nfr?: Partial<Record<NfrKey, string>>;
    },
    users: string[],
    team: string[],
    connections: Partial<Record<SourceId, SourceConnection>> = {}
): NewProductState {
    return {
        ...initialNewProductState,
        kind: "new",
        // Refresh mode does not render the form-factor pills; it inherits the
        // default, which is what the submitted payload already fell back to.
        primaryFormFactor: DEFAULT_PRIMARY_FORM_FACTOR,
        fields: { name: core.name, problem: core.problem, solution: core.solution },
        nfr: { ...emptyNfrValues(), ...(core.nfr ?? {}) },
        users: [...users],
        team: [...team],
        connections: { ...initialNewProductState.connections, ...connections }
    };
}

// v1 persisted drafts stored `connections` as Record<SourceId, boolean>; v2
// stores Record<SourceId, SourceConnection>; v3 adds optional NFR answers.
export function migratePersistedDraft(raw: { schema_version: number; state: Record<string, unknown> }): PersistedNewProductDraftState {
    if (raw.schema_version >= 2) {
        const state = raw.state as Omit<PersistedNewProductDraftState["state"], "nfr"> & { nfr?: Partial<Record<NfrKey, string>> };
        return {
            schema_version: 3,
            state: {
                ...state,
                primaryFormFactor: state.primaryFormFactor ?? DEFAULT_PRIMARY_FORM_FACTOR,
                nfr: { ...emptyNfrValues(), ...(state.nfr ?? {}) }
            }
        };
    }
    const v1conns = (raw.state.connections ?? {}) as Record<SourceId, boolean>;
    const connections = Object.fromEntries(
        (Object.keys(initialNewProductState.connections) as SourceId[]).map(k => [k, { connected: Boolean(v1conns[k]) }])
    ) as Record<SourceId, SourceConnection>;
    return {
        schema_version: 3,
        state: {
            ...(raw.state as PersistedNewProductDraftState["state"]),
            primaryFormFactor: DEFAULT_PRIMARY_FORM_FACTOR,
            nfr: emptyNfrValues(),
            connections
        }
    };
}

export function nfrValidationErrors(s: Pick<NewProductState, "nfr">): Partial<Record<NfrKey, string>> {
    const errors: Partial<Record<NfrKey, string>> = {};
    for (const field of NFR_FIELDS) {
        if (s.nfr[field.key].length > field.maxLength) {
            errors[field.key] = `${field.label} must be ${field.maxLength.toLocaleString()} characters or fewer.`;
        }
    }
    return errors;
}
